import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Phone, Mail, Calendar, CheckCircle2, AlertTriangle, Clock, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  overdue: "I Skaduar", due_soon: "Vjen Brenda Javës", upcoming: "Vjen Brenda 30 Ditësh", ok: "Në Rregull",
};
const STATUS_COLOR = {
  overdue: "bg-[#9E3C3C]/15 text-[#9E3C3C] border-[#9E3C3C]/30",
  due_soon: "bg-[#D4A24C]/20 text-[#8B6914] border-[#D4A24C]/40",
  upcoming: "bg-[#EAE4D9] text-[#2C4C3B] border-[#C9C2B2]",
  ok: "bg-[#4A8B71]/15 text-[#2C4C3B] border-[#4A8B71]/30",
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("sq-AL", { dateStyle: "medium" }) : "—";

export default function Recalls() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ overdue: 0, due_soon: 0, upcoming: 0, ok: 0, disabled: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [r, s] = await Promise.all([
      api.get("/recalls", { params: { only_due: false } }),
      api.get("/recalls/stats"),
    ]);
    setRows(r.data); setStats(s.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const markContacted = async (patientId) => {
    try {
      await api.post(`/patients/${patientId}/recall/mark-contacted`);
      toast.success("Pacienti u shënua si i kontaktuar");
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  return (
    <div className="space-y-8" data-testid="recalls-page">
      <div>
        <div className="label-eyebrow">Sistemi i Kujtuesve</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Kujtuesit (Recalls)</h1>
        <p className="mt-2 text-sm text-[#5C5C5C]">Pacientët që duhet të kontaktohen për vizitën e radhës periodike.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={AlertTriangle} label="Të Skaduar" value={stats.overdue} color="bg-[#9E3C3C]/15 text-[#9E3C3C]" testId="kpi-overdue" />
        <KPI icon={Clock} label="Brenda Javës" value={stats.due_soon} color="bg-[#D4A24C]/20 text-[#8B6914]" testId="kpi-due-soon" />
        <KPI icon={Calendar} label="Brenda 30 Ditësh" value={stats.upcoming} color="bg-[#EAE4D9] text-[#2C4C3B]" testId="kpi-upcoming" />
        <KPI icon={CheckCircle2} label="Në Rregull" value={stats.ok} color="bg-[#4A8B71]/15 text-[#2C4C3B]" testId="kpi-ok" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={16} className="text-[#5C5C5C]" />
        <Chip label="Të gjithë" active={filter === "all"} onClick={() => setFilter("all")} />
        <Chip label={`Të skaduar (${stats.overdue})`} active={filter === "overdue"} onClick={() => setFilter("overdue")} danger={stats.overdue > 0} />
        <Chip label="Brenda javës" active={filter === "due_soon"} onClick={() => setFilter("due_soon")} />
        <Chip label="Brenda 30 ditësh" active={filter === "upcoming"} onClick={() => setFilter("upcoming")} />
        <Chip label="Në rregull" active={filter === "ok"} onClick={() => setFilter("ok")} />
      </div>

      <div className="surface-card overflow-hidden" data-testid="recalls-table">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Duke ngarkuar…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa rezultate për këtë filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-6 py-3 label-eyebrow">Pacienti</th>
                <th className="px-6 py-3 label-eyebrow">Statusi</th>
                <th className="px-6 py-3 label-eyebrow">Vizita e Fundit</th>
                <th className="px-6 py-3 label-eyebrow">Recall i Radhës</th>
                <th className="px-6 py-3 label-eyebrow">Kontakti</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.patient_id} className="border-t border-[#E2DFD8]" data-testid={`recall-${r.patient_id}`}>
                  <td className="px-6 py-3">
                    <Link to={`/pacientet/${r.patient_id}`} className="font-medium hover:text-[#2C4C3B]">
                      {r.patient_name}
                    </Link>
                    <div className="text-xs text-[#5C5C5C]">{r.interval_months} muaj interval</div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium border ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                    {r.status === "overdue" && (
                      <div className="mt-1 text-xs text-[#9E3C3C]">{r.days_overdue} ditë vonesë</div>
                    )}
                    {r.status === "due_soon" && (
                      <div className="mt-1 text-xs text-[#8B6914]">për {r.days_until} ditë</div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm">{fmtDate(r.last_visit)}</td>
                  <td className="px-6 py-3 text-sm font-medium">{fmtDate(r.next_recall)}</td>
                  <td className="px-6 py-3 text-xs">
                    {r.phone && <div className="flex items-center gap-1"><Phone size={11} /> {r.phone}</div>}
                    {r.email && <div className="flex items-center gap-1 text-[#5C5C5C]"><Mail size={11} /> {r.email}</div>}
                    {!r.phone && !r.email && <span className="text-[#5C5C5C]">—</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => markContacted(r.patient_id)}
                            className="rounded-full px-3 py-1 text-xs font-medium bg-[#EAE4D9] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-[#FDFBF7] transition-colors"
                            data-testid={`mark-${r.patient_id}`}>
                      ✓ Kontaktuar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, active, onClick, danger }) {
  return (
    <button onClick={onClick}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active
                ? (danger ? "bg-[#9E3C3C] text-white" : "bg-[#2C4C3B] text-[#FDFBF7]")
                : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>{label}</button>
  );
}
